import React, { useEffect, useState } from 'react';
import { Server, Database, Globe, Shield, Mail, Upload, CheckCircle, XCircle, ChevronUp, RefreshCw } from 'lucide-react';
import { useApi } from './DashboardLayout';

export default function VersionPage() {
  const { apiFetch } = useApi();
  const [version, setVersion] = useState(null);
  const [sysInfo, setSysInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    const [vr, sr] = await Promise.all([
      apiFetch('/api/settings/version'),
      apiFetch('/api/settings/system-info'),
    ]);
    try { const vd = await vr?.json(); if (vd) setVersion(vd); } catch {}
    try { const sd = await sr?.json(); if (sd) setSysInfo(sd); } catch {}
    setLoading(false);
  };

  const incrementVersion = async (type) => {
    if (!version?.version) return;
    const parts = version.version.split('.').map(Number);
    if (type === 'major') { parts[0]++; parts[1] = 0; parts[2] = 0; }
    else if (type === 'minor') { parts[1]++; parts[2] = 0; }
    else { parts[2]++; }
    const newVersion = parts.join('.');
    setSaving(true); setMsg('');
    const r = await apiFetch('/api/settings/version', {
      method: 'PUT',
      body: JSON.stringify({ version: newVersion }),
    });
    if (r?.ok) {
      const d = await r.json();
      setVersion({ version: d.version, build_date: d.build_date });
      setMsg(`Version ${newVersion} gespeichert`);
    }
    setSaving(false);
    setTimeout(() => setMsg(''), 3000);
  };

  if (loading) return <div data-testid="system-info-page" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Laden...</div>;

  const dbOk = sysInfo?.database?.connected;
  const vParts = (version?.version || '1.0.0').split('.');

  return (
    <div data-testid="system-info-page">
      <div className="page-header">
        <h2>System-Info</h2>
        <p>Systemstatus und Versionsverwaltung</p>
      </div>

      {/* Version Card */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <h3><span className="card-header-icon" style={{ background: 'rgba(59,130,246,0.1)', color: 'var(--primary)' }}><Server size={18} /></span> App-Version</h3>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
            {/* Version Display */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {['Major', 'Minor', 'Patch'].map((label, i) => (
                <React.Fragment key={label}>
                  {i > 0 && <span style={{ fontSize: 28, fontWeight: 300, color: 'var(--text-muted)', margin: '0 2px' }}>.</span>}
                  <div style={{ textAlign: 'center' }}>
                    <button onClick={() => incrementVersion(label.toLowerCase())} disabled={saving}
                      data-testid={`version-${label.toLowerCase()}-btn`}
                      style={{
                        width: 52, height: 52, borderRadius: 10, border: '2px solid var(--border-color, #e0e0e0)',
                        background: 'var(--bg)', cursor: 'pointer', display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s',
                      }}>
                      <ChevronUp size={12} style={{ color: 'var(--text-muted)', marginBottom: -2 }} />
                      <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>{vParts[i] || '0'}</span>
                    </button>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4, fontWeight: 600 }}>{label}</div>
                  </div>
                </React.Fragment>
              ))}
            </div>

            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              <div><strong>Version:</strong> {version?.version || '1.0.0'}</div>
              {version?.build_date && <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>Letzte Änderung: {new Date(version.build_date + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</div>}
            </div>

            {msg && <span style={{ fontSize: 13, fontWeight: 600, color: '#10B981' }}>{msg}</span>}
          </div>
        </div>
      </div>

      {/* Status Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Database */}
        <div className="card">
          <div className="card-header">
            <h3><span className="card-header-icon" style={{ background: dbOk ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', color: dbOk ? '#10B981' : '#EF4444' }}><Database size={18} /></span> MariaDB</h3>
            <StatusBadge ok={dbOk} />
          </div>
          <div className="card-body">
            <InfoRow label="Host" value={sysInfo?.database?.host || '–'} />
            <InfoRow label="Datenbank" value={sysInfo?.database?.database || '–'} mono />
            <InfoRow label="Tabellen" value={sysInfo?.database?.tables || 0} />
            {sysInfo?.database?.error && <InfoRow label="Fehler" value={sysInfo.database.error} error />}
          </div>
        </div>

        {/* Deployment */}
        <div className="card">
          <div className="card-header">
            <h3><span className="card-header-icon" style={{ background: 'rgba(99,102,241,0.1)', color: '#6366F1' }}><Globe size={18} /></span> Deployment</h3>
          </div>
          <div className="card-body">
            <InfoRow label="Plattform" value={sysInfo?.platform || 'Emergent'} />
            <InfoRow label="Domain" value={sysInfo?.domain || '–'} link={`https://${sysInfo?.domain}`} />
            <InfoRow label="Python" value={sysInfo?.python_version || '–'} mono />
          </div>
        </div>

        {/* Services */}
        <div className="card">
          <div className="card-header">
            <h3><span className="card-header-icon" style={{ background: 'rgba(245,158,11,0.1)', color: '#F59E0B' }}><Shield size={18} /></span> Dienste</h3>
          </div>
          <div className="card-body">
            <ServiceRow label="SMTP (E-Mail)" ok={sysInfo?.smtp_configured} icon={Mail} />
            <ServiceRow label="FTP/SFTP" ok={sysInfo?.ftp_configured} icon={Upload} />
          </div>
        </div>

        {/* CORS */}
        <div className="card">
          <div className="card-header">
            <h3><span className="card-header-icon" style={{ background: 'rgba(16,185,129,0.1)', color: '#10B981' }}><Shield size={18} /></span> CORS-Domains</h3>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{sysInfo?.cors_origins?.length || 0} Einträge</span>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
              {(sysInfo?.cors_origins || []).map((origin, i) => (
                <div key={i} style={{
                  padding: '8px 16px', fontSize: 12, fontFamily: 'monospace', color: 'var(--text-secondary)',
                  borderBottom: i < sysInfo.cors_origins.length - 1 ? '1px solid var(--border-color, #f0f0f0)' : 'none',
                }}>
                  {origin}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Refresh */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn" onClick={loadData} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, border: '1px solid var(--border-color, #ddd)' }}>
          <RefreshCw size={13} /> Aktualisieren
        </button>
      </div>
    </div>
  );
}

function StatusBadge({ ok }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700,
      padding: '3px 10px', borderRadius: 20,
      background: ok ? '#D1FAE5' : '#FEE2E2',
      color: ok ? '#065F46' : '#991B1B',
    }}>
      {ok ? <CheckCircle size={12} /> : <XCircle size={12} />}
      {ok ? 'Verbunden' : 'Fehler'}
    </span>
  );
}

function InfoRow({ label, value, mono, link, error }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border-color, #f5f5f5)' }}>
      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{label}</span>
      {link ? (
        <a href={link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, fontWeight: 500, color: 'var(--primary)', textDecoration: 'none' }}>{value}</a>
      ) : (
        <span style={{ fontSize: 13, fontWeight: 500, fontFamily: mono ? 'monospace' : 'inherit', color: error ? '#EF4444' : 'var(--text)' }}>{value}</span>
      )}
    </div>
  );
}

function ServiceRow({ label, ok, icon: Icon }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-color, #f5f5f5)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
        <Icon size={14} style={{ color: 'var(--text-muted)' }} />
        {label}
      </div>
      <span style={{
        fontSize: 11, fontWeight: 600, color: ok ? '#10B981' : '#9CA3AF',
        display: 'flex', alignItems: 'center', gap: 4,
      }}>
        {ok ? <CheckCircle size={12} /> : <XCircle size={12} />}
        {ok ? 'Konfiguriert' : 'Nicht konfiguriert'}
      </span>
    </div>
  );
}
